import { FC } from 'react';
import { useSelector } from 'react-redux';
import { Tag } from 'antd';

import './NotificationItem.scss';
import { EllipseIcon, EllipseRedIcon } from 'src/assets/icons';
import Avatar from 'src/components/avatar';
import { BaseText } from 'src/components/typography';
import { TNotification } from 'src/interfaces/notification-interface';
import { TRootState } from 'src/stores';
import { getTimeToDay } from 'src/utils/common-utils';
import { ENotificationTriggerType, EUserType } from 'src/variables/enum-variables';
import { WarningOutlined } from '@ant-design/icons';

interface INotificationItemProps {
  notificationItemDetail: TNotification;
  onClickToNotificationItem: (notificationItem: TNotification) => Promise<void>;
}

const getMessageLabel = (content: string) => {
  try {
    const resultObj = JSON.parse(content);
    const predictedLabel = resultObj.body?.predictedLabel || resultObj.predictedLabel;
    return `${predictedLabel}`;
  } catch (error) {
    // Silent fail
    return null;
  }
};

// Map severity level to colors
const getSeverityColor = (label: string | null) => {
  if (!label) return { color: 'default', textColor: '#000000' };
  
  switch (label) {
    case 'Mild Distress':
      return { color: 'orange', textColor: '#994c00' };
    case 'Moderate Distress':
      return { color: 'volcano', textColor: '#663300' };
    case 'Severe Distress':
      return { color: 'red', textColor: '#8c0000' };
    case 'Immediate Danger':
      return { color: 'red', textColor: '#b30000' };
    default:
      return { color: 'default', textColor: '#000000' };
  }
};

const NotificationItem: FC<INotificationItemProps> = ({ notificationItemDetail, onClickToNotificationItem }) => {
  const { senderProfile, senderClient, createdAt, isRead, triggerType, content, metadata } = notificationItemDetail;
  const sender = senderProfile ?? senderClient;
  const profile = useSelector((state: TRootState) => state.user.profile);
  const messageLabel = content ? getMessageLabel(content) : null;
  const { color, textColor } = getSeverityColor(messageLabel);
  const isDistressAlert = triggerType === ENotificationTriggerType.MESSAGE_WARNING || 
                         triggerType === ENotificationTriggerType.HOMEWORK_WARNING;

  const getNotificationText = () => {
    const senderName = `${sender?.firstName} ${sender?.lastName}`;
    switch (triggerType) {
      case ENotificationTriggerType.NEW_MESSAGE:
        return (
          <div>
            <BaseText type="button" inline textAlign="left">
              {senderName}&nbsp;
            </BaseText>
            <BaseText type="body2" inline textAlign="left">
              sent you a message
            </BaseText>
          </div>
        );
      case ENotificationTriggerType.MESSAGE_WARNING:
        return (
          <div className="NotificationItem__alert">
            <div className="NotificationItem__alert-header">
              <BaseText type="button" inline textAlign="left">
                <WarningOutlined style={{ marginRight: '5px', color: '#ff4d4f' }} />
                Distress Alert - AI Chat
              </BaseText>
              <Tag color={color}>{messageLabel}</Tag>
            </div>
            <BaseText type="body2" textAlign="left">
              {senderName} sent a message to jAImee that requires attention.
              {metadata?.messageContent && (
                <div className="NotificationItem__alert-preview">
                  &quot;{metadata.messageContent.substring(0, 50)}{metadata.messageContent.length > 50 ? '...' : ''}&quot;
                </div>
              )}
            </BaseText>
          </div>
        );
      case ENotificationTriggerType.HOMEWORK_WARNING:
        return (
          <div className="NotificationItem__alert">
            <div className="NotificationItem__alert-header">
              <BaseText type="button" inline textAlign="left">
                <WarningOutlined style={{ marginRight: '5px', color: '#ff4d4f' }} />
                Distress Alert
              </BaseText>
              <Tag color={color}>{messageLabel}</Tag>
            </div>
            <BaseText type="body2" textAlign="left">
              {senderName} submitted a homework response that requires attention.
            </BaseText>
          </div>
        );
      case ENotificationTriggerType.CLIENT_ACCEPT_INVITE:
      case ENotificationTriggerType.PRACTITIONER_ACCEPT_INVITE:
        return (
          <div>
            <BaseText type="button" inline textAlign="left">
              {senderName}&nbsp;
            </BaseText>
            <BaseText type="body2" inline textAlign="left">
              accepted your invitation&nbsp;
            </BaseText>
          </div>
        );
      case ENotificationTriggerType.HOMEWORK_COMPLETED:
        return (
          <div>
            <BaseText type="button" inline textAlign="left">
              {senderName}&nbsp;
            </BaseText>
            <BaseText type="body2" inline textAlign="left">
              has completed an assigned homework task
            </BaseText>
          </div>
        );
      case ENotificationTriggerType.CLIENT_REALLOCATE:
        return (
          <div>
            <BaseText type="body2" textAlign="left" inline>
              {Number(content)} client{Number(content) > 1 ? 's ' : ' '}
              {Number(content) > 1 ? 'have' : 'has'} been allocated to you by clinic {profile?.clinic?.name}
            </BaseText>
          </div>
        );
      default:
        break;
    }
  };

  return (
    <div className={`NotificationItem ${isDistressAlert ? 'NotificationItem--alert' : ''}`} onClick={() => onClickToNotificationItem(notificationItemDetail)}>
      <div className="NotificationItem__avatar">
        <Avatar src={sender?.avatar} />
      </div>
      <div className="NotificationItem__content">
        {getNotificationText()}
        <BaseText type="x-small" inline textAlign="left" className="NotificationItem__content-time">
          {getTimeToDay(createdAt)}
        </BaseText>
      </div>
      {(!isRead && isDistressAlert) ? (
        <div className="NotificationItem__dot">
          <EllipseRedIcon width={12} height={12} />
        </div>
      ) : (
        <div className="NotificationItem__dot">
          {!isRead && <EllipseIcon width={10} height={10} />}
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
